import React, { useEffect, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import {
  BackButton,
  Box,
  ButtonStyled,
  FormElementWrapper,
  NotAuthorizedForResource,
  LabelStyled,
  SpanStyled,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../../../components/styled/Elements";
import { useTranslation } from "react-i18next";
import { ProfileWrapper } from "./styled";
import {
  MenuTriggerCross,
  PlaceholderImg,
} from "../../../../../components/styled/AllImages";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import FormRadio from "../../../../../components/common/radioButtons/FormRadio";
import Breadcrumbs from "../../../../../components/admin/Breadcrumbs/Breadcrumbs";
import CabinetsApi from "../../../../../services/api/cabinetsApi";
import { toast } from "react-toastify";
import { ContentWrapper } from "../../../../../components/styled/styled";
import * as Yup from "yup";
import RequestsApi from "../../../../../services/api/requestsApi";
import { getIsTemporary } from "../../../../../utils";
import AccessSettings from "../../components/AccessSettings";
import { useSelector } from "react-redux";
import { ValidatedInputWrapper } from "../../../../../components/common/inputFields/ValidatedInput";
import { FormGroup } from "react-bootstrap";

const ASSET_URL = process.env.REACT_APP_ASSET_URL;

const EditFile = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { usersList, branchesDeps, hqBranchesList } = useSelector(
    (state) => state.helper,
  );
  const navigate = useNavigate();
  const isTemporary = getIsTemporary();
  const { id, fileId } = useParams();

  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [accessThroughRequest, setAccessThroughRequest] = useState([]);

  const validationSchema = Yup.object().shape(
    {
      name: Yup.string().required(
        t("file_name_required") || "File name is required",
      ),

      // 1. My Department ID
      // Required only if BOTH other_department_id AND setting_id are empty
      my_department_id: Yup.number()
        .nullable()
        .when(["other_department_id", "setting_id"], {
          is: (otherId, settingId) => !otherId && !settingId,
          then: (schema) => schema.required(t("Pleaseselectone")),
          otherwise: (schema) => schema.notRequired(),
        }),

      // 2. Other Department ID
      // Required only if BOTH my_department_id AND setting_id are empty
      other_department_id: Yup.number()
        .nullable()
        .when(["my_department_id", "setting_id"], {
          is: (myId, settingId) => !myId && !settingId,
          then: (schema) => schema.required(t("Pleaseselectone")),
          otherwise: (schema) => schema.notRequired(),
        }),

      // 3. Setting ID (Myself)
      // Required only if BOTH my_department_id AND other_department_id are empty
      setting_id: Yup.number()
        .nullable()
        .when(["my_department_id", "other_department_id"], {
          is: (myId, otherId) => !myId && !otherId,
          then: (schema) => schema.required(t("Pleaseselectone")),
          otherwise: (schema) => schema.notRequired(),
        }),

      // Ids selection validations
      my_dept_ids: Yup.array()
        .ensure()
        .when("my_department_id", {
          is: (val) => val === 4 || val === 5,
          then: (schema) =>
            schema
              .min(1, t("Please_select_user"))
              .required(t("Please_select_user")),
          otherwise: (schema) => schema.notRequired(),
        }),

      other_dept_ids: Yup.array()
        .ensure()
        .when("other_department_id", {
          is: (val) => [6, 7, 8, 9].includes(val),
          then: (schema) =>
            schema.min(1, t("Required")).required(t("Required")),
          otherwise: (schema) => schema.notRequired(),
        }),
    },
    [
      // Cyclic dependencies array
      ["my_department_id", "other_department_id"],
      ["my_department_id", "setting_id"],
      ["other_department_id", "setting_id"],
    ],
  );

  // Fetch file data
  const getCabinetSingleFile = async () => {
    try {
      setAccessDenied(false);
      setLoading(true);
      const response = await CabinetsApi.getCabinetSingleFile(id, fileId);

      // Check for 403 early
      if (response?.code === 403) {
        setAccessDenied(true);
        return;
      }

      if (response?.data) {
        const fileData = response.data.file;
        const allReferrals = fileData?.referrals || [];

        let my_department_id = null;
        let my_dept_ids = [];
        let other_department_id = null;
        let other_dept_ids = [];
        let common_settings_id = null;
        let from_branches = [];
        let from_departments = [];
        let checkedUsers = [];

        fileData?.cabinetfile_details?.forEach((detail) => {
          if (detail.group_type === "my_department") {
            my_department_id = detail.common_settings_id;
            my_dept_ids = detail.selected_users || [];
          } else if (detail.group_type === "other_department") {
            other_department_id = detail.common_settings_id;
            other_dept_ids =
              detail.selected_users || detail.selected_departments || [];
            from_branches = detail.from_branches || [];
            from_departments = detail.from_departments || [];

            // Fixed the reference error here: Use 'allReferrals' instead of 'response.data.cabinet.referrals'
            checkedUsers =
              detail?.selected_users_data?.map((item) => {
                const uKey = `${detail.group_type}_${item.id}`;
                const matchingReferral = allReferrals.find(
                  (ref) =>
                    ref.referred_to_id === item.id &&
                    ref.type === detail.group_type,
                );

                return {
                  ...item,
                  subLabel:
                    language === "en"
                      ? `${item?.user_detail?.department_selective?.name_en} (${item?.user_detail?.office_location_selective?.name_en})`
                      : `${item?.user_detail?.department_selective?.name_ar} (${item?.user_detail?.office_location_selective?.name_ar})`,
                  referralUniqueKey: uKey,
                  referralNote: matchingReferral ? matchingReferral.note : "",
                  shareReferrals: !!matchingReferral,
                };
              }) || [];
          } else if (
            detail.group_type === "myself" ||
            detail.group_type === "base_cabinet"
          ) {
            common_settings_id = detail.common_settings_id;
          }
        });

        setInitialValues({
          action: "update",
          file_id: fileData?.id,
          name: fileData?.name || "",
          description: fileData?.description || "",
          is_temporary: isTemporary && isTemporary,
          setting_id: fileData.common_settings_id || common_settings_id,
          my_department_id: my_department_id,
          my_dept_ids: my_dept_ids,
          other_department_id: other_department_id,
          other_dept_ids: other_dept_ids,
          referrals: allReferrals?.map((ref) => ({
            unique_id: `${ref.type}_${ref.referred_to_id}`,
            user_id: ref.referred_to_id,
            is_share: 1,
            note: ref.note,
            group: ref.type,
          })),
          checkedUsers: checkedUsers,
          from_branches: from_branches,
          from_departments: from_departments,
          checkedHqBranches: {
            branchIds: from_branches,
            depIds: from_departments,
          },
        });
      }
    } catch (error) {
      console.error("Error fetching file:", error);
      toast.error(t("Error fetching file"));
    } finally {
      setLoading(false);
    }
  };
  // Handle remove request
  const handleRemoveRequest = async (requestId) => {
    try {
      const { code, message } = await RequestsApi.removeRequest({
        id: requestId,
      });
      if (code === 200) {
        setAccessThroughRequest(
          accessThroughRequest.filter((item) => item.id !== requestId),
        );
        toast.success(t("Request removed successfully"));
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {
      console.log("Error removing request:", error);
    }
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      const submitValues = {
        ...values,
        common_settings_id: values.setting_id ?? null,
      };
      console.log("submitValues: ", submitValues);
      // return 
      const response = await CabinetsApi.createCabinetFile(id, submitValues);
      if (response.code === 200) {
        toast.success(t("file_updated"));
        navigate(`/cabinets/${id}/files`);
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
    } catch (error) {
      console.log("Error updating file:", error);
      toast.error(t("Error updating file"));
    } finally {
      setSubmitting(false);
    }
  };

  // Load file data on mount
  useEffect(() => {
    getCabinetSingleFile();
  }, [id]);

  if (loading) {
    return (
      <ProfileWrapper>
        <ThemeLoader />
      </ProfileWrapper>
    );
  }

  if (accessDenied) {
    return <NotAuthorizedForResource />;
  }

  if (!initialValues) {
    return (
      <ProfileWrapper>
        <ThemeLoader />
      </ProfileWrapper>
    );
  }

  return (
    <ProfileWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("edit_file")}
            onClick={() => navigate(`/cabinets/${id}/files`)}
          />
          <Breadcrumbs />
        </Box>
      </Box>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ isSubmitting, errors, values }) => {
          return (
            <Form>
              <ContentWrapper>
                {/* File Name Field */}
                <Box>
                  <ValidatedInputWrapper>
                    <FormGroup className="formEleContainer">
                      <LabelStyled
                        className="border-0"
                        size="15px"
                        color="var(--text)"
                      >
                        {t("file_name")}
                        <span className="staric">*</span>
                      </LabelStyled>

                      <Box className="w-100">
                        <Field
                          type="text"
                          name="name"
                          className="form-control"
                          placeholder="File Name"
                        />
                        <ErrorMessage
                          name="name"
                          component="div"
                          className="text-danger text-sm mb-0 mt-1"
                        />
                      </Box>
                    </FormGroup>
                  </ValidatedInputWrapper>

                  {/* Description Field */}
                  <ValidatedInputWrapper>
                    <FormGroup className="formEleContainer">
                      <LabelStyled
                        className="border-0"
                        size="15px"
                        color="var(--text)"
                      >
                        {t("Description")}
                      </LabelStyled>

                      <Field
                        as="textarea"
                        type="textarea"
                        name="description"
                        className="form-control"
                        placeholder="Description"
                        style={{ height: "130px" }}
                      />

                      <ErrorMessage
                        name="description"
                        component="div"
                        className="invalid-feedback d-block"
                      />
                    </FormGroup>
                  </ValidatedInputWrapper>
                </Box>

                {/* Access Settings */}
                <Box>
                  <Box className="innerFoHead">
                    <SubHeadingStyled size="16px" color="var(--text)">
                      {t("file_access_settings")}
                    </SubHeadingStyled>
                  </Box>

                  <AccessSettings
                    isEdit={true}
                    showBaseCabinetOption={true}
                  />
                </Box>

                {/* Access Granted Through Request */}
                {accessThroughRequest && accessThroughRequest.length > 0 && (
                  <Box>
                    <Box className="innerFoHead">
                      <SubHeadingStyled size="16px" color="var(--text)">
                        {t("Access_granted_through_request")}
                      </SubHeadingStyled>
                    </Box>
                    <FormElementWrapper className="d-flex gap-2 flex-wrap">
                      {accessThroughRequest.map((item) => (
                        <UserUpload key={item.id}>
                          <Box className="userPlaceholder">
                            <img
                              src={item?.profile || PlaceholderImg}
                              alt="user"
                            />
                          </Box>
                          <Box className="abtUser">
                            <SpanStyled color="var(--text)" size="14px">
                              {item?.from_user?.name}
                            </SpanStyled>
                          </Box>
                          <MenuTriggerCross
                            className="removeTaskTrigger"
                            onClick={() => handleRemoveRequest(item?.id)}
                          />
                        </UserUpload>
                      ))}
                    </FormElementWrapper>
                  </Box>
                )}

                {/* Action Buttons */}
                <Box className="d-flex justify-content-end">
                  <Box className="p-4 d-flex gap-3">
                    <ButtonStyled
                      label={t("Cancel")}
                      bg={"var(--bgSecondary)"}
                      color={"var(--text2)"}
                      type="button"
                      onClick={() => navigate(`/cabinets/${id}/files`)}
                    />
                    <ButtonStyled
                      label={t("Update")}
                      type="submit"
                      isLoading={isSubmitting}
                    />
                  </Box>
                </Box>
              </ContentWrapper>
            </Form>
          );
        }}
      </Formik>
    </ProfileWrapper>
  );
};

export default EditFile;

const UserUpload = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  padding: 8px 16px;
  background-color: var(--bgSecondary);
  border-radius: 10px;
  width: fit-content;
  .userPlaceholder {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    max-width: 30px;
    height: 30px;
    background-color: var(--greyBg);
    border-radius: 50%;
    overflow: hidden;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  svg.removeTaskTrigger {
    width: 12px;
    height: 12px;
    margin-left: 7px;
    &:hover {
      stroke: var(--redColor);
      fill: var(--redColor);
      path {
        stroke: var(--redColor);
        fill: var(--redColor);
      }
    }
  }
  input[type="file"] {
    opacity: 0;
    position: absolute;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
  .abtUser {
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
  }
`;
